import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import AutoCompletedInput from 'components/common/autoCompletedInput';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import isNil from 'lodash/isNil';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { autosuggestHighlightParse } from 'utils/misc';
import { useGetGooglePlaces, useGoogleMapGeoCode } from 'components/common/hooks';
import { AddressDTO } from 'artifacts/dtos';
import Spinner from 'components/spinner/Spinner';

export type Place = Partial<google.maps.places.AutocompletePrediction> | null;

type Props = {
  onChange?: (value: AddressDTO, event?: ChangeEvent<HTMLInputElement>) => void;
  value?: Place;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
};

export default function AddressSuggestion({
  onChange,
  value: _value,
  onBlur,
  disabled,
  className,
  autoFocus,
}: Props) {
  const [value, setValue] = useState<Place | undefined>(_value);
  const [address, setAddress] = useState<string | undefined>('');
  const [inputValue, setInputValue] = useState('');

  const {
    data: options,
    isFetching: loadingPlaces,
  } = useGetGooglePlaces(inputValue);

  const { data, hasError: geoCodeError, isLoading: loadingGeoCode } = useGoogleMapGeoCode(address ?? '');
  const isLoading = loadingGeoCode || loadingPlaces;

  useEffect(() => {
    if (!geoCodeError && !isNil(data)) {
      onChange?.(data);
    }
  }, [data]);

  useEffect(() => {
    if (!isNil(_value)) {
      setValue?.(_value);
    }
  }, [_value]);

  const onInputChange = useCallback(debounce((
    event: SyntheticEvent,
    value: string,
  ) => {
    if (!isNil(event)) {
      setInputValue(value);
    }
  }, 700), []);

  return (
    <AutoCompletedInput<Place>
      id="address-suggestion"
      className={clsx('!pointer-events-auto', className)}
      onBlur={onBlur}
      getLabel={(option) => (option?.description ?? '')}
      options={options ?? []}
      filterOptions={(x) => x}
      clearOnBlur={false}
      loading={loadingPlaces}
      value={value ?? null}
      filterSelectedOptions
      noOptionsText="No locations"
      placeholder="Enter Address"
      clearIcon={<CloseIcon className="!h-4 !w-4 -m-0.5" />}
      popupIcon={isLoading ? <Spinner color="#9ca3af" name="ClipLoader" size={16} className="pr-1" /> : undefined}
      onChange={(value) => {
        const newValue = value as Place;
        setValue?.(newValue);
        setAddress(newValue?.description);
      }}
      disabled={disabled}
      onInputChange={onInputChange}
      renderOption={(props, option) => {
        const matches = option?.structured_formatting?.main_text_matched_substrings ?? [];
        const parts = autosuggestHighlightParse(
          option?.structured_formatting?.main_text ?? '',
          matches.map((match) => [match.offset, match.offset + match.length]),
        );
        return (
          <li {...props} className="flex items-center hover:bg-gray-100 !text-xs !my-2 cursor-pointer">
            <div className="mx-1">
              <LocationOnIcon className="text-gray-400 !text-base" />
            </div>
            <div className="w-[calc(100%-44px)]">
              {parts.map((part, index) => (
                <span
                  key={`${option?.place_id}_${index + 1}`}
                  className={clsx({ 'font-semibold': part.highlight })}
                >
                  {part.text}
                </span>
              ))}
              <p className=" text-gray-400">
                {option?.structured_formatting?.secondary_text}
              </p>
            </div>
          </li>
        );
      }}
      InputProps={{
        autoFocus,
      }}
    />
  );
}
